import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import toast from 'react-hot-toast'
import { updateOrders } from '@/app/actions'
import { useDeliveryAppContext } from '@/context/deliveryApp/DeliveryAppContext'
import { usePostPostponedOrder } from '@/hooks/graphql/deliverOrder'
import { Spinner } from '@/components/LayoutApp/Spinner'
import Alert from '@/components/Screens/CurrentOrderScreen/Alert'
import {
  getCustomerAddress,
  thereIsAnotherOrderToDeliver
} from '@/utils/dataUtils'

const PostponedModal = ({ isOpen, closeModal }) => {
  const [toastId, setToastId] = useState(null)
  const { ordersToday } = useDeliveryAppContext()

  const nextOrderToDeliver =
    ordersToday?.find((order) => order?.status_delivery_van === 'pending') ||
    null

  const anotherOrderToDeliver = thereIsAnotherOrderToDeliver(
    ordersToday,
    nextOrderToDeliver
  )

  const { address, location } = getCustomerAddress(nextOrderToDeliver)

  const [loading, setLoading] = useState(false)

  const {
    postPostponedOrder,
    data: postponedOrder,
    error
  } = usePostPostponedOrder()

  const handleClick = () => {
    setLoading(true)
    try {
      updateOrders()
      postPostponedOrder({
        order_id: Number(nextOrderToDeliver?.id)
      })
      const toastId = toast.loading('Postergando orden...')
      setToastId(toastId)
    } catch (e) {
      console.error(e)
      toast.error(
        'No se ha podido postergar la orden, al parecer no hay conexión a internet'
      )
    }
  }

  useEffect(() => {
    if (postponedOrder) {
      try {
        updateOrders()
      } catch (e) {
        console.error(e)
      }
      toast.success('Orden postergada!', { id: toastId })
    }
    if (error) {
      toast.error(
        'No se ha podido postergar la orden, intente nuevamente, si el problema persiste contacte a soporte.',
        {
          id: toastId
        }
      )
    }
  }, [postponedOrder, error])

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="bg-black/50 dark:bg-gray-darkMode/75 fixed inset-0" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="rounded-2xl dark:bg-black-darkMode flex flex-col items-center justify-between w-full h-auto max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl">
                  <Dialog.Title
                    as="h3"
                    className="dark:text-white-darkMode w-full p-3 mb-3 text-lg font-medium leading-6 text-center text-gray-700 border-b"
                  >
                    Posponer entrega
                  </Dialog.Title>

                  <div className="text-payne-primary dark:text-white-darkMode my-6 text-sm text-center">
                    <h2 className="px-2 py-2 text-3xl font-bold">
                      Orden {nextOrderToDeliver?.orden_camioneta}
                      {nextOrderToDeliver?.van_order_letter}
                    </h2>
                    <h3 className="text-sm font-medium leading-5">{address}</h3>
                    <h4 className="text-xs font-medium leading-5">
                      {location}
                    </h4>
                    {
                      <p className="font-extralight p-2 text-sm">
                        #{nextOrderToDeliver?.id} -{' '}
                        {nextOrderToDeliver?.customer?.nombre}{' '}
                        {nextOrderToDeliver?.customer?.apellido}
                      </p>
                    }
                  </div>
                  <div className="w-full pb-4">
                    {anotherOrderToDeliver && (
                      <Alert
                        key={'another-order'}
                        alert={'Hay otra orden en la misma dirección'}
                      />
                    )}
                  </div>

                  <div className="flex flex-col w-full">
                    <button
                      disabled={loading}
                      type="button"
                      className={`${
                        loading
                          ? 'bg-gray-500 dark:bg-gray-darkMode/75 border-gray-500 dark:border-gray-500'
                          : 'bg-orange-primary dark:bg-orange-darkMode border-orange-primary '
                      } hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md`}
                      onClick={handleClick}
                    >
                      {loading ? (
                        <Spinner className="animate-spin dark:text-gray-600 fill-gray-700 inline w-6 h-6 mr-2 text-gray-200" />
                      ) : (
                        'Posponer'
                      )}
                    </button>
                    <button
                      type="button"
                      className="dark:bg-black-darkMode dark:text-white-darkMode/60 text-payne-primary hover:opacity-80 inline-flex justify-center px-4 py-2 mt-2 text-sm font-medium bg-white rounded-md"
                      onClick={closeModal}
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
export default PostponedModal
